import { ArrowRight, Award, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { siteData, images } from "../data/mock";

const messageTemplate = `Hi Dr. Kang, I'd like to book an appointment for [Name] at your [Location] office. My preferred time is [Date/Time].`;

export const HeroSection = () => {
  const handleBookAppointment = () => {
    const encodedMessage = encodeURIComponent(messageTemplate);
    window.location.href = `sms:${siteData.clinic.smsNumber}?body=${encodedMessage}`;
  };

  return (
    <section
      aria-label="Welcome to The Acupuncture & Fire Cupping Clinic"
      className="relative min-h-[92vh] flex items-center pt-20 lg:pt-0 overflow-hidden"
      style={{
        background:
          "linear-gradient(145deg, #FFF5EB 0%, #FFFAF5 35%, #FFFFFF 70%, #FFF9F2 100%)",
      }}
    >
      <div
        className="absolute top-20 right-[-100px] w-[500px] h-[500px] rounded-full bg-[#E8EFE8]/30 blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-[-50px] left-[-100px] w-[400px] h-[400px] rounded-full bg-[#FFF0E0]/40 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-12 lg:py-20">
          <div className="order-2 lg:order-1 animate-fadeInUp">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <Badge
                variant="secondary"
                className="bg-[#F0F4ED] text-[#5E8C61] border border-[#C5D4C0]/60 px-3.5 py-1.5 text-xs font-medium rounded-full"
              >
                <Shield className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                Licensed in CA & HI
              </Badge>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-semibold text-[#232323] leading-[1.08] tracking-tight mb-6"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
            >
              Precision Healing.{" "}
              <span className="text-[#5E8C61]">Lasting Relief.</span>
            </h1>

            <p className="text-lg lg:text-xl text-stone-500 leading-relaxed max-w-xl mb-8">
              {siteData.hero.subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                onClick={handleBookAppointment}
                size="lg"
                className="bg-[#232323] hover:bg-[#353535] text-white rounded-full px-8 h-13 text-base font-medium shadow-lg shadow-stone-900/10 hover:shadow-stone-900/15 transition-all duration-200"
                data-testid="hero-book-appointment-btn"
              >
                {siteData.hero.primaryCta.label}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-[#5E8C61] text-[#4A7A4D] hover:bg-[#F0F4ED] hover:border-[#4A7A4D] rounded-full px-8 h-13 text-base transition-all duration-200"
                data-testid="hero-intake-form-btn"
              >
                <a href={siteData.clinic.intakeUrl} target="_blank" rel="noopener noreferrer">
                  New Patient Form
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-stone-500">
              <div className="flex items-center gap-1.5">
                <Award className="w-4 h-4 text-[#BCA182]" aria-hidden="true" />
                <span>CA & HI Licensed Acupuncturist</span>
              </div>
              <div
                className="w-px h-4 bg-stone-300 hidden sm:block"
                aria-hidden="true"
              />
              <div className="flex items-center gap-1.5">
                <Award className="w-4 h-4 text-[#BCA182]" aria-hidden="true" />
                <span>CA Certified Massage Therapist</span>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative animate-fadeIn" style={{ animationDelay: "0.2s" }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-stone-900/10">
              <img
                src="https://images.pexels.com/photos/3865780/pexels-photo-3865780.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Acupuncture treatment in a warm, calming clinical setting at Pure Wave Acupuncture"
                className="w-full h-[340px] sm:h-[420px] lg:h-[480px] object-cover"
                loading="eager"
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-stone-900/10 via-transparent to-transparent"
                aria-hidden="true"
              />
            </div>

            <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-stone-100/80 p-4 max-w-[210px] animate-fadeInUp" style={{ animationDelay: "0.5s" }}>
              <div className="flex items-center gap-0.5 mb-1.5" aria-label="5 out of 5 stars">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-3.5 h-3.5 fill-current text-[#BCA182]"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-xs text-stone-600 font-medium leading-relaxed">
                &ldquo;Pain improved by 60% after first treatment&rdquo;
              </p>
              <p className="text-[10px] text-stone-400 mt-1">&mdash; Sarah M.</p>
            </div>

            <div className="absolute -top-3 -right-2 sm:-right-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-md border border-stone-100/80 px-3.5 py-2.5 animate-fadeIn" style={{ animationDelay: "0.7s" }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#F0F4ED] flex items-center justify-center">
                  <Shield className="w-4 h-4 text-[#5E8C61]" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-[#232323] leading-tight">Multi-State</p>
                  <p className="text-[10px] text-stone-500 leading-tight">Licensed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
